// App.jsx - Updated with new theme and structure
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7
} from '@mui/icons-material';
import Sidebar from './components/Sidebar/Sidebar';
import SearchDashboard from './components/SearchDashboard/SearchDashboard';
import { useCarrierData } from './hooks/useCarrierData';
import './styles/globals.css';

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          // Light theme
          primary: {
            main: '#2563eb',
            light: '#3b82f6',
            dark: '#1d4ed8'
          },
          secondary: {
            main: '#059669',
            light: '#10b981',
            dark: '#047857'
          },
          background: {
            default: '#f8fafc',
            paper: '#ffffff',
          },
          text: {
            primary: '#1e293b',
            secondary: '#64748b',
          },
          grey: {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
          }
        }
      : {
          // Dark theme
          primary: {
            main: '#3b82f6',
            light: '#60a5fa',
            dark: '#2563eb'
          },
          secondary: {
            main: '#10b981',
            light: '#34d399',
            dark: '#059669'
          },
          background: {
            default: '#0f172a',
            paper: '#1e293b',
          },
          text: {
            primary: '#f1f5f9',
            secondary: '#cbd5e1',
          },
          grey: {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
          }
        }),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid',
          borderColor: mode === 'light' ? '#e2e8f0' : '#334155',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

const drawerWidth = 280;

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [searchHistory, setSearchHistory] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  useEffect(() => {
    setDarkMode(prefersDarkMode);
  }, [prefersDarkMode]);

  const theme = React.useMemo(() => 
    createTheme(getDesignTokens(darkMode ? 'dark' : 'light')),
    [darkMode]
  );

  const { carrierData, loading, error, searchCarrier, batchSearch, clearBatchResults, showBasicInfo, toggleInfoView } = useCarrierData();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (term, type, filters = {}) => {
    const defaultFilters = {
      skipBrokers: false,
      skipInactive: false,
      showInactiveOnly: false,
      activeDuration: 0,
      startNumber: '',
      endNumber: '',
      resultLimit: 10
    };
    
    const finalFilters = { ...defaultFilters, ...filters };
    
    searchCarrier(term, type, finalFilters);
    
    const newSearch = {
      term,
      type,
      timestamp: new Date().toLocaleString(),
      result: carrierData ? 'Found' : 'Not Found',
      filters: finalFilters
    };
    
    setSearchHistory(prev => [newSearch, ...prev.slice(0, 9)]);
    localStorage.setItem('searchHistory', JSON.stringify([newSearch, ...searchHistory.slice(0, 9)]));
  };

  const handleBatchSearch = (batchInput, filters = {}) => {
    const searchTerms = batchInput.split('\n').filter(term => term.trim());
    
    if (searchTerms.length > 0) {
      batchSearch(batchInput, filters);
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${drawerWidth}px` },
            backgroundColor: 'background.paper',
            color: 'text.primary',
            boxShadow: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            
                     <Typography variant="h5" sx={{ color: '#6366f1', fontWeight: 'bold' }}>
                       Carrier Search Pro
                     </Typography>
                     <Typography variant="body2" color="text.secondary">
                       Advanced FMCSA carrier database search
                     </Typography>
                   
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={darkMode}
                  onChange={toggleDarkMode}
                  icon={<Brightness7 sx={{ color: 'grey.400' }} />}
                  checkedIcon={<Brightness4 />}
                />
              }
              label={darkMode ? 'Dark' : 'Light'}
              sx={{ mr: 1 }}
            />
          </Toolbar>
        </AppBar>

        {/* Sidebar */}
        <Sidebar
          drawerWidth={drawerWidth}
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          searchHistory={searchHistory}
          onSearchFromHistory={handleSearch}
          darkMode={darkMode}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            minHeight: '100vh',
            backgroundColor: 'background.default',
          }}
        >
          <Toolbar />

          {activeTab === 'search' && (
            <SearchDashboard
              onSearch={handleSearch}
              onBatchSearch={handleBatchSearch}
              clearBatchResults={clearBatchResults}
              loading={loading}
              error={error}
              carrierData={carrierData}
              showBasicInfo={showBasicInfo}
              onToggleView={toggleInfoView}
              darkMode={darkMode}
            />
          )}

          {activeTab === 'history' && (
            <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                mb: 4
              }}>
                Search History
              </Typography>
              
              {searchHistory.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 8,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <History sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" color="text.secondary">
                    No search history yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Your search queries will appear here
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  {searchHistory.map((search, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 3,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: darkMode ? 'grey.800' : 'grey.50',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.05)'
                        }
                      }}
                      onClick={() => handleSearch(search.term, search.type)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {search.term}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            backgroundColor: 
                              search.type === 'mc' ? 'primary.main' : 
                              search.type === 'dot' ? 'secondary.main' : 'warning.main',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          {search.type.toUpperCase()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Searched on {search.timestamp}
                      </Typography>
                      {search.filters && Object.values(search.filters).some(val => val) && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          {search.filters.skipBrokers && (
                            <Typography variant="caption" sx={{ 
                              backgroundColor: darkMode ? 'grey.700' : 'grey.200',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1
                            }}>
                              No Brokers
                            </Typography>
                          )}
                          {search.filters.skipInactive && (
                            <Typography variant="caption" sx={{ 
                              backgroundColor: darkMode ? 'grey.700' : 'grey.200',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1
                            }}>
                              Active Only
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;